import { Button, Card, message, Modal, Space, Table } from 'antd';
import React, { useState } from 'react';
import useSWR from 'swr';
import { z } from 'zod';

import EditCategories from '@/components/UI/desktop/antd/EditCategories';
import { getAxiosWithAuth } from '@/lib/axios';
import sendNotification from '@/lib/sendMessage';
import { feedbackSchema } from '@/types/database/feedback';

import FeedbackEditModal from '../FeedbackEditModal';
import type { FeedbackEditModalProps } from '../FeedbackEditModal/types';
import FeedbackFilter from '../FeedbackFilter';
import MessageModal from '../MessageModal';
import { generateColumns } from './helpers';
import type { FeedbackSearchQuery } from './types';
import { useOptions } from './useOptions';

const FeedbackList = () => {
  const [showModal, setShowEditModal] = useState<'edit' | 'more' | null>(null);
  const [editingFeedback, setEditingFeedback] =
    useState<FeedbackEditModalProps['editingFeedback']>(null);
  const [query, setQuery] = useState<FeedbackSearchQuery>({
    page: 1,
    pageSize: 10,
    order: 'descend',
    orderField: 'status',
  });

  const { householdNumberOptions, typeOptions, updateCategories } =
    useOptions();

  const { data: feedbacksResponseData, mutate } = useSWR(
    query,
    async () => {
      const axios = getAxiosWithAuth();
      const { queryTimes, ...rest } = query;
      const requestQuery: Omit<typeof query, 'queryTimes'> & {
        queryTimes?: [string, string];
      } = {
        ...rest,
      };
      if (queryTimes) {
        requestQuery.queryTimes = [
          queryTimes[0].startOf('day').toISOString(),
          queryTimes[1].endOf('day').toISOString(),
        ];
      }
      const res = await axios.get('/ezcon/api/feedbacks', {
        params: requestQuery,
      });
      const responseDataSchema = z.object({
        total: z.number(),
        data: z.array(feedbackSchema),
      });
      const validData = responseDataSchema.parse(res.data);
      return validData;
    },
    // refresh every 60 seconds, keep previous data to prevent flickering
    { refreshInterval: 60 * 1000, keepPreviousData: true }
  );

  const columns = generateColumns({
    onEdit: (feedback) => {
      setEditingFeedback(feedback);
      setShowEditModal('edit');
    },
    onDelete: (feedback) => {
      Modal.confirm({
        title: '確定要刪除嗎？',
        content: '刪除後將無法復原',
        onOk: async () => {
          const axios = getAxiosWithAuth();
          await axios.delete(`/ezcon/api/feedbacks/${feedback._id}`);
          mutate();
          message.success('刪除成功');
        },
      });
    },
    onReply: (feedback) => {
      setEditingFeedback(feedback);
      setShowEditModal('more');
    },
    onNotify: async (feedback) => {
      await sendNotification({
        class: 'feedback',
        title: '意見反應通知',
        content: `${feedback.householdNumber}住戶您好,您的意見反應已經更新，請點擊查看。`,
        query: {
          householdNumbers: {
            $in: [feedback.householdNumber],
          },
          firebaseClass: '意見反應',
        },
        redirect: '/ezpro/feedback/feedbackFrontDesk',
      });
      message.success('通知成功');
    },
    order: query.order,
    orderField: query.orderField,
    setOrderField: (orderField) => {
      setQuery(({ order, orderField: prevField, ...rest }) => {
        let newOrder: typeof order = 'descend';
        if (orderField === prevField) {
          newOrder = order === 'descend' ? 'ascend' : 'descend';
        }
        return {
          ...rest,
          order: newOrder,
          orderField,
        };
      });
    },
  });

  const feedbacks = feedbacksResponseData?.data.map((feedback) => ({
    ...feedback,
    key: feedback._id,
  }));

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Space direction="horizontal">
        <Button
          onClick={() => {
            setShowEditModal('edit');
          }}
        >
          新增意見反應
        </Button>
        <EditCategories
          buttonLabel="編輯意見反應類別"
          categoryName="意見反應"
          modalTitle="編輯意見反應類別"
          onUpdate={() => {
            updateCategories();
            setQuery((prev) => ({ ...prev, type: undefined, page: 1 }));
          }}
        />
      </Space>
      <FeedbackFilter
        householdNumberOptions={householdNumberOptions}
        typeOptions={typeOptions}
        query={query}
        setQuery={setQuery}
      />
      <Card>
        <Table
          columns={columns}
          dataSource={feedbacks}
          pagination={{
            current: query.page,
            total: feedbacksResponseData?.total,
            onChange: (page) => {
              setQuery((prev) => ({ ...prev, page }));
            },
            showSizeChanger: false,
          }}
          bordered
          size="small"
          showSorterTooltip={false}
          scroll={{ x: 800 }}
        />
      </Card>
      <FeedbackEditModal
        editingFeedback={editingFeedback}
        open={showModal === 'edit'}
        onClose={() => {
          setShowEditModal(null);
          setEditingFeedback(null);
        }}
        update={mutate}
        typeOptions={typeOptions}
        householdNumberOptions={householdNumberOptions}
      />
      <MessageModal
        feedback={editingFeedback}
        open={showModal === 'more'}
        onClose={() => {
          setShowEditModal(null);
          setEditingFeedback(null);
        }}
      />
    </Space>
  );
};

export default FeedbackList;
